// MACHINERY V2 (Stroje) — the tab-toolbar select (M2.5).
//
// Reference impl: design_handoff_machinery_v3/machinery-profile-parts.jsx
// (`PcSelect`) — a 32px control with a mono uppercase eyebrow, the current value
// and a chevron; artboards 1009 (desktop) and 1013 (phone).
//
// RADIX, NOT `<select>` AND NOT MUI. §3 pins the v2 primitive library to Radix;
// a native `<select>` cannot carry the eyebrow-inside-the-control layout the
// artboard draws, and MUI is out.
//
// PORTAL RULE (MachineryV2Layout.tsx:20-39): DropdownMenu.Content portals to
// document.body, outside `.machinery-v2-root`, where the scoped vars do not
// resolve. Content re-carries the scope classes, sets a transparent background,
// paints on the inner `.ms-menu` div and reads `palette` LIVE on every render.
// Same countermeasure as chrome/ChromeRightMenus.tsx `MenuContent` (canonical)
// and fleet/FleetCardMenu.tsx.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";

import { useAppSelector } from "../../../../../shared/hooks/sharedHooks";

export interface TabSelectOption {
  id: string;
  name: string;
}

export interface TabSelectProps {
  label: string;
  /** The "no filter" row label, e.g. common:all. */
  anyLabel: string;
  value?: string;
  options: TabSelectOption[];
  onChange: (value?: string) => void;
}

const TabSelect: React.FC<TabSelectProps> = ({ label, anyLabel, value, options, onChange }) => {
  const palette = useAppSelector((s) => s.machineryV2Ui.palette);
  const selected = options.find((o) => o.id === value);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={label}
          style={{
            height: 32,
            padding: "0 10px",
            borderRadius: 7,
            minWidth: 140,
            maxWidth: 220,
            background: "var(--card)",
            border: "1px solid var(--border)",
            display: "flex",
            alignItems: "center",
            gap: 8,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 8.5,
              letterSpacing: "0.07em",
              textTransform: "uppercase",
              color: "var(--faint)",
              flexShrink: 0,
            }}
          >
            {label}
          </span>
          <span
            style={{
              fontSize: 12.5,
              color: "var(--ink-2)",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {selected ? selected.name : anyLabel}
          </span>
          <svg
            viewBox="0 0 12 12"
            width="11"
            height="11"
            fill="none"
            stroke="var(--muted)"
            strokeWidth="1.5"
            strokeLinecap="round"
            style={{ marginLeft: "auto", flexShrink: 0 }}
            aria-hidden
          >
            <path d="M3 4.5 L6 7.5 L9 4.5" />
          </svg>
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={`machinery-v2-root p-${palette} mod-stroje`}
          align="start"
          sideOffset={6}
          onClick={(e) => e.stopPropagation()}
          style={{ background: "transparent", zIndex: 1290 }}
        >
          <div
            className="ms-menu"
            role="none"
            style={{ width: 240, maxHeight: 320, overflowY: "auto" }}
          >
            <DropdownMenu.Item className="mi" onSelect={() => onChange(undefined)}>
              <span>{anyLabel}</span>
            </DropdownMenu.Item>
            {options.map((o) => (
              <DropdownMenu.Item
                key={o.id}
                className="mi"
                onSelect={() => onChange(o.id)}
                style={o.id === value ? { color: "var(--mod-accent)", fontWeight: 600 } : undefined}
              >
                <span>{o.name}</span>
              </DropdownMenu.Item>
            ))}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default TabSelect;
